import { useEffect, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { Box, IconButton, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ContainerWrapper from "../../components/ContainerWrapper/ContainerWrapper";
import PatientDetailPanel from "./PatientDetailPanel";
import PatientVideoDialog from "./PatientVideoDialog";
import { fetchUtente } from "../../services/user";
import { normalizePatient } from "../../utils/patient";
import { useUserState } from "../../state/user";
import Loading from "../../components/Loading/Loading";

export default function PatientDetail() {
  const history = useHistory();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const { token } = useUserState();
  const [patient, setPatient] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  // The scheduling modal lives in the URL (?video=1) so the browser back button
  // closes it instead of leaving the patient page.
  const videoDialogOpen =
    new URLSearchParams(location.search).get("video") === "1";

  useEffect(() => {
    if (!token || !id) return;
    (async () => {
      try {
        // The endpoint nests everything under clinicalFile.details; without
        // flattening it the panel below reads undefined for every field and
        // renders a card of dashes.
        const data = normalizePatient(await fetchUtente(token, id));
        if (!data) {
          history.replace("/patients");
          return;
        }
        setPatient(data);
      } catch {
        history.replace("/patients");
      } finally {
        setLoading(false);
      }
    })();
  }, [token, id, history]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" mt={10}>
        <Loading />
      </Box>
    );
  }

  if (!patient) return null;

  const firstName = patient.name?.split(" ")[0] ?? "";

  return (
    <>
      <Box
        sx={{
          background: "linear-gradient(135deg, #00BFA5, #009688)",
          padding: "12px 16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between"
        }}
      >
        <IconButton
          onClick={() => history.push("/patients")}
          sx={{ color: "#fff" }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography
          sx={{
            color: "#fff",
            fontWeight: 700,
            fontSize: "18px",
            letterSpacing: "1px"
          }}
        >
          {firstName.toUpperCase()}
        </Typography>
        <Box sx={{ width: 40 }} />
      </Box>

      <ContainerWrapper>
        <PatientDetailPanel
          patient={patient}
          onVideoCall={() =>
            history.push({ pathname: location.pathname, search: "?video=1" })
          }
          onClinicalRecord={() =>
            history.push(`/patients/${id}/clinical-records`, { patient })
          }
        />
      </ContainerWrapper>

      <PatientVideoDialog
        patientName={patient.name ?? ""}
        patient={patient}
        open={videoDialogOpen}
        onClose={() =>
          history.replace({ pathname: location.pathname, search: "" })
        }
      />
    </>
  );
}
